/**
 * Route definition for /collection/$chain/$slug
 *
 * Uses TanStack Router's loader to prefetch collection data server-side.
 * Fetches collection information and the first page of NFTs before render.
 * The chain parameter is captured but not currently used (can be used for
 * multi-chain support in the future).
 *
 * @module routes/collection/$chain/$slug
 * @category Routes
 */

import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getCollectionInfo, searchNFTs, ApiError } from '@/lib/api'
import CollectionContainer from '@/containers/Collection'
import { createCollectionSchema, createItemListSchema, createBreadcrumbListSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'
import { t } from '@/i18n/translator'
import chainData from '@/chains'
import Breadcrumb from '@/components/Breadcrumb'

export const Route = createFileRoute('/$locale/collection/$chain/$slug')({
  loader: async ({ params: { chain, slug } }) => {
    if (!slug) {
      throw new Error('Collection slug is required')
    }

    const isNumeric = /^\d+$/.test(slug)
    const isEthAddress = /^0x[a-fA-F0-9]{40}$/.test(slug)

    try {
      let collectionInfo: any
      let initialNfts: any[] = []

      const nftOptions = { offset: 0, limit: 40, order: 'listings' as const }

      if (isNumeric) {
        const collId = Number(slug)
        ;[collectionInfo, initialNfts] = await Promise.all([
          getCollectionInfo({ collId }),
          searchNFTs(collId, nftOptions),
        ])
        initialNfts = initialNfts.items
      } else if (isEthAddress) {
        ;[collectionInfo, initialNfts] = await Promise.all([
          getCollectionInfo({ contractAddress: slug, chainName: chain }),
          searchNFTs(slug, { ...nftOptions, canonicalName: slug, chainName: chain }),
        ])
        initialNfts = initialNfts.items
      } else {
        ;[collectionInfo, initialNfts] = await Promise.all([
          getCollectionInfo({ canonicalName: slug, chainName: chain }),
          searchNFTs(slug, { ...nftOptions, canonicalName: slug, chainName: chain }),
        ])
        initialNfts = initialNfts.items
      }

      const collId = collectionInfo?.id
      if (collId === undefined || collId === null) {
        throw new Error(`Collection resolved but numeric id is missing for "${slug}"`)
      }

      return {
        initialCollectionInfo: collectionInfo,
        initialNfts,
        slug,
        chain,
        collectionId: collId,
      }
    } catch (error) {
      console.error('Failed to load collection:', error)
      toast.error('Failed to load collection. Please try again.')
      throw error
    }
  },

  component: CollectionPage,
})

function CollectionPage() {
  const { initialCollectionInfo, initialNfts, slug, chain, collectionId } = Route.useLoaderData() as {
    initialCollectionInfo: any
    initialNfts: any
    slug: string
    chain: string
    collectionId: string | number
  }

  const pageUrl = chain
    ? `https://stealthisnft.xyz/collection/${chain}/${slug}`
    : `https://stealthisnft.xyz/collection/${slug}`

  const collectionName = initialCollectionInfo?.title || initialCollectionInfo?.name || slug
  const chainEntry = Array.isArray(chainData)
    ? chainData.find((entry: any) => entry && String(entry.chainId) == String(initialCollectionInfo?.chain))
    : null
  const chainName = chainEntry?.chainName || chainEntry?.network || null
  const chainId = chainEntry?.chainId

  return (
    <CollectionContainer
      collectionId={String(collectionId)}
      initialCollectionInfo={initialCollectionInfo}
      initialNfts={initialNfts}
      initialVolData={initialCollectionInfo}
      collectionName={collectionName}
      logo={chainId ? `/assets/img/chain-logos/chain-${chainId}.png` : undefined}
    />
  )
}
